import {
    Avatar,
    Box,
    Chip,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import dayjs from 'dayjs';
import {
    SupportRequestActivity,
    SupportRequestPriority,
    SupportRequestStatus,
    SupportRequestType,
} from '../../apiClients/SupportClient';

export const supportStatusLabel = (status: SupportRequestStatus) => ({
    Submitted: 'Submitted',
    WaitingForClient: 'Waiting for client',
    Triaged: 'Triaged',
    Planned: 'Planned',
    InProgress: 'In progress',
    Resolved: 'Resolved',
    NotPlanned: 'Not planned',
    Duplicate: 'Duplicate',
    Closed: 'Closed',
}[status] || status);

export const StatusChip = ({ status }: { status: SupportRequestStatus }) => {
    const color = status === 'Resolved' || status === 'Closed'
        ? 'success'
        : status === 'NotPlanned' || status === 'Duplicate'
            ? 'default'
            : status === 'WaitingForClient'
                ? 'warning'
                : status === 'InProgress'
                    ? 'info'
                    : 'primary';
    return <Chip size="small" color={color as any} variant={status === 'Submitted' ? 'outlined' : 'filled'} label={supportStatusLabel(status)} />;
};

export const PriorityChip = ({ priority }: { priority: SupportRequestPriority }) =>
    <Chip
        size="small"
        variant="outlined"
        color={priority === 'Urgent' ? 'error' : priority === 'High' ? 'warning' : 'default'}
        label={priority}
    />;

export const RequestTypeIcon = ({ type, small = false }: { type: SupportRequestType; small?: boolean }) =>
    <Avatar
        sx={{
            width: small ? 30 : 40,
            height: small ? 30 : 40,
            bgcolor: type === 'Issue' ? 'error.main' : 'info.main',
        }}>
        {type === 'Issue' ? <BugReportOutlinedIcon fontSize={small ? 'small' : 'medium'} /> : <LightbulbOutlinedIcon fontSize={small ? 'small' : 'medium'} />}
    </Avatar>;

export const ActivityTimeline = ({ activity = [], showInternal = false }: { activity?: SupportRequestActivity[]; showInternal?: boolean }) => {
    const visible = activity.filter(item => showInternal || !item.IsInternal);
    if (!visible.length)
        return <Typography color="text.secondary">No updates have been posted yet.</Typography>;

    return <Stack spacing={0}>
        {[...visible].sort((a, b) => dayjs(b.CreatedDate).valueOf() - dayjs(a.CreatedDate).valueOf()).map((item, index) =>
            <Box key={item.Id || `${item.CreatedDate}-${index}`} sx={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 1.5 }}>
                <Stack alignItems="center">
                    <Avatar sx={{ width: 28, height: 28, bgcolor: item.IsInternal ? 'warning.main' : 'primary.main', fontSize: 12 }}>
                        {item.IsInternal ? <LockOutlinedIcon sx={{ fontSize: 15 }} /> : (item.ActorName || 'S').slice(0, 1).toUpperCase()}
                    </Avatar>
                    {index < visible.length - 1 && <Divider orientation="vertical" flexItem sx={{ minHeight: 32 }} />}
                </Stack>
                <Box sx={{ pb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={700} variant="body2">{item.ActorName || 'Synergist Pro'}</Typography>
                        {item.IsInternal && <Chip size="small" label="Internal" color="warning" variant="outlined" />}
                        <Typography variant="caption" color="text.secondary">{dayjs(item.CreatedDate).format('MMM D, YYYY h:mm A')}</Typography>
                    </Stack>
                    {(item.FromStatus || item.ToStatus) &&
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.FromStatus ? supportStatusLabel(item.FromStatus) : 'Created'} → {item.ToStatus ? supportStatusLabel(item.ToStatus) : item.ActivityType}
                        </Typography>}
                    {item.Message && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{item.Message}</Typography>}
                </Box>
            </Box>)}
    </Stack>;
};
